import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useStores } from '../../hooks/use-stores';
import ErrorMessage from '../../components/errormessage';
import CircularProgress from '@mui/material/CircularProgress';
import PartnerWebhookFormDef from '../../forms/partnerWebhook';
import {
    Block,
    FlexDiv,
    TextDefault,
    BigModal,
    Heading2,
    ThemedButton,
    DarkArrowTooltipIcon,
    TextTiny,
    Tag,
} from '@awarego-ui/components';
import TableClient from '../../components/table/TableClient';
import RequireRole from '../RequireRole';
import WebhookFormFields from '../../components/forms/WebhookFormFields';
import WebhookTestDialog from '../../components/forms/WebhookTestDialog';
import IntegrationDetailSidebar from '../../components/company/integration-detail-sidebar';
import Modal from 'react-modal';
import SendIcon from '@mui/icons-material/Send';
import services from '../../services';
import {extractErrorMessage} from "@awarego-ui/my/src/utils/helpers";

function PartnerWebhooks() {
    const { partnerId } = useParams();
    const { commonStore } = useStores();
    const rowsPerPage = 5;

    const [webhooks, setWebhooks] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [loadingWebhooks, setLoadingWebhooks] = useState(true);
    const [deletingWebhooks, setDeletingWebhooks] = useState([]);
    const [error, setError] = useState(null);
    const [isModalOpen, setModalIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    // The webhook whose test dialog is open, or null.
    const [testingWebhook, setTestingWebhook] = useState(null);

    useEffect(() => {
        Modal.setAppElement('body');
    }, []);

    useEffect(() => {
        loadWebhooks();
        loadCompanies();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerId]);

    // useLocalObservable's factory runs only once (on mount), so the onSuccess
    // hook below captures whatever `doAddOrEditWebhook` reference existed at
    // that first render — which itself closed over that render's `partnerId`.
    // If this component isn't remounted when the :partnerId route param
    // changes (React Router doesn't remount just because a param changes),
    // every future save would silently go to the FIRST partner ever loaded.
    // Routing all calls through a ref that's reassigned every render (the
    // "latest ref" pattern) makes onSuccess always invoke the current
    // partnerId/loadWebhooks/loadCompanies, regardless of when the form was
    // created.
    const doAddOrEditWebhookRef = useRef();

    const localStore = useLocalObservable(() => {
        let webhookForm = new PartnerWebhookFormDef(
            {},
            {
                onSuccess() {
                    doAddOrEditWebhookRef.current(this);
                },
                onError(form) {
                    console.error('error', form.errors());
                },
            }
        );

        return { webhookForm };
    });

    const loadWebhooks = async () => {
        setLoadingWebhooks(true);
        setError(null);
        try {
            const result = await services.Partners
                .webhooksService(partnerId)
                .list();
            setWebhooks(result || []);
        } catch (e) {
            setWebhooks([]);
            setError('Failed to load webhooks.');
        } finally {
            setLoadingWebhooks(false);
        }
    };

    const loadCompanies = async () => {
        try {
            const result = await services.Partners
                .companiesService(partnerId)
                .list();
            setCompanies(result || []);
        } catch (e) {
            setCompanies([]);
        }
    };

    const doAddOrEditWebhook = async (form) => {
        // Re-entrancy guard: a double-click (or a slow request) before the
        // first save resolves must not create/update the webhook twice.
        if (isSaving) return;
        setIsSaving(true);

        const values = form.values();
        const payload = {
            name: values.name,
            url: values.url,
            signatureSecret: values.signatureSecret,
            enabled: values.enabled,
            type: values.allTypes ? ['all'] : values.types,
            companyId:
                values.company && values.company !== 'all'
                    ? Number(values.company)
                    : null,
        };

        try {
            if (values.id && values.id !== -1) {
                await services.Partners
                    .webhooksService(partnerId)
                    .update(payload, values.id);
            } else {
                await services.Partners
                    .webhooksService(partnerId)
                    .create(payload);
            }
            setModalIsOpen(false);
            await loadWebhooks();
        } catch (e) {
            commonStore.error(
                extractErrorMessage(e) || 'Failed to save webhook'
            );
        } finally {
            setIsSaving(false);
        }
    };
    // Kept fresh every render so the mobx form's onSuccess hook (captured only
    // once, see above) always calls through to the latest partnerId/state.
    doAddOrEditWebhookRef.current = doAddOrEditWebhook;

    const createNew = () => {
        openModal({
            id: -1,
            name: '',
            url: '',
            status: 1,
            definition: { eventName: [] },
        });
    };

    const openModal = (webhook) => {
        const eventNames = Array.isArray(webhook.definition?.eventName)
            ? webhook.definition.eventName
            : webhook.definition?.eventName
            ? [webhook.definition.eventName]
            : ['all'];
        const isAll = eventNames.includes('all');

        // Load per field via .set() (like the report) — update() breaks the
        // array field's binding.
        const f = localStore.webhookForm;
        f.$('id').set(webhook.id);
        f.$('name').set(webhook.name || '');
        f.$('url').set(webhook.url || '');
        f.$('signatureSecret').set(webhook.definition?.signatureSecret || '');
        f.$('enabled').set('value', webhook.status === 1);
        f.$('company').set(
            webhook.companyId ? String(webhook.companyId) : 'all'
        );
        f.$('allTypes').set('value', isAll);
        f.$('types').set(isAll ? [] : eventNames);
        f.$('types').set('disabled', isAll);
        f.resetValidation();
        f.validate();
        setModalIsOpen(true);
    };

    const deleteKey = (id) => {
        commonStore.showConfirm(
            'Do you really want to delete this Webhook?',
            'Delete',
            'Delete Webhook?',
            async () => {
                setDeletingWebhooks((prev) => [...prev, id]);
                try {
                    await services.Partners
                        .webhooksService(partnerId)
                        .delete(id);
                    commonStore.success('Webhook deleted!');
                    await loadWebhooks();
                } catch (e) {
                    commonStore.error(
                        e.response?.data?.msg || 'Failed to delete webhook'
                    );
                } finally {
                    setDeletingWebhooks((prev) =>
                        prev.filter((x) => x !== id)
                    );
                }
            }
        );
    };

    const companyNameById = useMemo(() => {
        const map = {};
        companies.forEach((c) => {
            map[c.id] = c.name;
        });
        return map;
    }, [companies]);

    const columns = useMemo(() => {
        return [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: observer((x) => (
                    <>
                        {x.value && x.value.length > 0 ? (
                            x.value
                        ) : (
                            <Tag text="No Name" />
                        )}{' '}
                    </>
                )),
            },
            {
                Header: 'Url',
                accessor: 'url',
                Cell: observer((x) => <TextTiny>{x.value}</TextTiny>),
                disableGlobalFilter: true,
            },
            {
                Header: 'Company',
                accessor: 'companyId',
                Cell: observer((x) => (
                    <TextTiny>
                        {x.value
                            ? companyNameById[x.value] || `#${x.value}`
                            : 'All companies'}
                    </TextTiny>
                )),
                disableGlobalFilter: true,
            },
            {
                Header: '',
                accessor: 'id',
                // FlexDiv wraps by default, so a long name or url squeezes this
                // cell until the action icons stack onto several lines. Keeping
                // them on one line also makes the column's min-content width the
                // width of the icons, so the table stops shrinking it.
                Cell: observer((x) => (
                    <FlexDiv $noWrap style={{ whiteSpace: 'nowrap' }}>
                        {deletingWebhooks.includes(x.value) ? (
                            <CircularProgress />
                        ) : (
                            buildExtraActionsColumn(x.row.original)
                        )}
                    </FlexDiv>
                )),
                disableGlobalFilter: true,
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deletingWebhooks, companyNameById]);

    const buildExtraActionsColumn = (row) => {
        return (
            <RequireRole>
                {/* Only an enabled webhook can be tested - a disabled one is
                    refused by the API, since delivery is off for it. */}
                {row.status === 1 && (
                    <DarkArrowTooltipIcon
                        customFunction={() => setTestingWebhook(row)}
                        row={row}
                        title="Test Webhook"
                        icon={<SendIcon />}
                    ></DarkArrowTooltipIcon>
                )}
                <DarkArrowTooltipIcon
                    customFunction={() => openModal(row)}
                    row={row}
                    title="Edit"
                    icon={'edit'}
                ></DarkArrowTooltipIcon>
                <DarkArrowTooltipIcon
                    customFunction={() => deleteKey(row.id)}
                    row={row}
                    title="Delete Webhook"
                    icon={'delete'}
                ></DarkArrowTooltipIcon>
            </RequireRole>
        );
    };

    return (
        <FlexDiv>
            <Block flexGrow="9" mr="20">
                <ErrorMessage error={error} />
                <FlexDiv column>
                    <Heading2 mb={8}>Webhooks</Heading2>
                    <TextDefault>
                        Partner-level webhooks receive notifications on actions
                        and modifications of trainings and assessments. Leave the
                        company empty to receive events from all your companies,
                        or pick one to scope the webhook to it.
                    </TextDefault>
                </FlexDiv>
                <FlexDiv pt="32" pb="32">
                    <ThemedButton
                        onClick={createNew}
                        mode="primary"
                        text="Create Webhook"
                    />
                </FlexDiv>
                {loadingWebhooks && <CircularProgress />}
                <TableClient
                    columns={columns}
                    initiallyLoaded={loadingWebhooks === false}
                    data={[...webhooks]}
                    noItemFoundText={'No existing webhooks'}
                    hidePagination={webhooks.length <= rowsPerPage}
                />
            </Block>

            <FlexDiv column flexGrow="3" style={{ alignSelf: 'flex-start' }}>
                <IntegrationDetailSidebar
                    href="https://help.awarego.com/webhooks"
                    description="Learn more on how to use webhooks."
                />
            </FlexDiv>

            <BigModal
                ariaHideApp={false}
                isOpen={isModalOpen}
                width={600}
                onRequestClose={() => setModalIsOpen(false)}
                heading={
                    localStore.webhookForm.$('id').value === -1
                        ? 'Create Webhook'
                        : 'Edit Webhook'
                }
                modalContent={
                    <WebhookFormFields
                        form={localStore.webhookForm}
                        companies={companies}
                    />
                }
                modalFooterContent={
                    <Fragment>
                        <ThemedButton
                            mode="primary"
                            text={
                                isSaving
                                    ? 'Saving'
                                    : localStore.webhookForm.$('id').value ===
                                      -1
                                    ? 'Create'
                                    : 'Save'
                            }
                            $saving={isSaving}
                            $center={true}
                            disabled={
                                !localStore.webhookForm.isValid || isSaving
                            }
                            onClick={(e) => {
                                localStore.webhookForm.submit(e);
                            }}
                        />
                        <ThemedButton
                            mode="secondary"
                            onClick={() => setModalIsOpen(false)}
                            text="Close"
                        />
                    </Fragment>
                }
            />

            <WebhookTestDialog
                isOpen={!!testingWebhook}
                webhook={testingWebhook}
                onClose={() => setTestingWebhook(null)}
                onSend={(eventName) =>
                    services.Partners
                        .webhooksService(partnerId)
                        .test(testingWebhook.id, eventName)
                }
            />
        </FlexDiv>
    );
}

export default observer(PartnerWebhooks);
